import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/Card.jsx";
import { scoringApi } from "../../api/scoringApi";
import { getApiError } from "../../api/client";
import { Save, RefreshCw } from "lucide-react";
import "../styles/codebase.css";

export default function ScoringRulesPage() {
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [baseScore, setBaseScore] = useState(100);
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);

  const [wCritical, setWCritical] = useState(15);
  const [wHigh, setWHigh] = useState(8);
  const [wMedium, setWMedium] = useState(3);
  const [wLow, setWLow] = useState(1);

  const payload = useMemo(() => ({
    base_score: Number(baseScore),
    min_score: Number(minScore),
    max_score: Number(maxScore),
    weights_json: {
      critical: Number(wCritical),
      high: Number(wHigh),
      medium: Number(wMedium),
      low: Number(wLow),
    },
  }), [baseScore, minScore, maxScore, wCritical, wHigh, wMedium, wLow]);

  const load = async () => {
    setErr("");
    setInfo("");
    setLoading(true);
    try {
      const res = await scoringApi.get();
      const cfg = res.data || {};
      setBaseScore(cfg.base_score ?? 100);
      setMinScore(cfg.min_score ?? 0);
      setMaxScore(cfg.max_score ?? 100);

      const w = cfg.weights_json || {};
      setWCritical(w.critical ?? 15);
      setWHigh(w.high ?? 8);
      setWMedium(w.medium ?? 3);
      setWLow(w.low ?? 1);
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setErr("");
    setInfo("");
    setSaving(true);
    try {
      await scoringApi.update(payload);
      setInfo("✅ Scoring rules updated");
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="cb-page">
      <div className="page-head">
        <div>
          <h2>Scoring Rules</h2>
          <div className="muted">Configure Repo Health score weights (Critical/High/Medium/Low).</div>
        </div>

        <div className="page-actions">
          <button className="btn" onClick={load} disabled={loading}>
            <RefreshCw size={16} /> {loading ? "Loading…" : "Refresh"}
          </button>
          <button className="btn primary" onClick={save} disabled={loading || saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}
      {info && <div className="info">{info}</div>}

      <Card title="Health score formula">
        <div className="muted small">
          score = base_score − (critical × wCritical) − (high × wHigh) − (medium × wMedium) − (low × wLow),
          then clamped to min_score..max_score
        </div>

        <div className="grid-2" style={{ marginTop: 12 }}>
          <div className="field">
            <label>Base Score</label>
            <input type="number" value={baseScore} onChange={(e) => setBaseScore(e.target.value)} />
          </div>
          <div className="field">
            <label>Min Score</label>
            <input type="number" value={minScore} onChange={(e) => setMinScore(e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label>Max Score</label>
            <input type="number" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} />
          </div>
          <div className="field">
            <label>—</label>
            <div className="muted small">Keep min &lt;= max</div>
          </div>
        </div>

        <div className="grid-2" style={{ marginTop: 8 }}>
          <div className="field">
            <label>Critical Weight</label>
            <input type="number" value={wCritical} onChange={(e) => setWCritical(e.target.value)} />
          </div>
          <div className="field">
            <label>High Weight</label>
            <input type="number" value={wHigh} onChange={(e) => setWHigh(e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label>Medium Weight</label>
            <input type="number" value={wMedium} onChange={(e) => setWMedium(e.target.value)} />
          </div>
          <div className="field">
            <label>Low Weight</label>
            <input type="number" value={wLow} onChange={(e) => setWLow(e.target.value)} />
          </div>
        </div>
      </Card>

      <Card title="Current payload preview">
        <pre className="mono small" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
          {JSON.stringify(payload, null, 2)}
        </pre>
      </Card>
    </div>
  );
}
